import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Link,
  Container,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Alert,
  Stack,
  Tooltip,
  Fade
} from '@mui/material';
import {
  Key as KeyIcon,
  Link as LinkIcon,
  Shield as ShieldIcon,
  IntegrationInstructions as IntegrationIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as EyeIcon,
  VisibilityOff as EyeOffIcon,
  ContactSupport as SupportIcon,
  CheckCircle as CheckIcon,
  Code as ApiIcon,
  Security as SecurityIcon,
  Code as CodeIcon,
  Webhook as WebhookIcon,
  AutoAwesome as SparkleIcon,
  Description as DocsIcon,
  RocketLaunch as RocketIcon,
  Speed as SpeedIcon,
  Autorenew as AutoIcon
} from '@mui/icons-material';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { useUserData } from '../hooks/useUserData';
import * as amplitude from '@amplitude/analytics-browser';

const API = () => {
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookUrlChanged, setWebhookUrlChanged] = useState(false);
  const [showDeleteKeyDialog, setShowDeleteKeyDialog] = useState(false);
  const [showDeleteSecretDialog, setShowDeleteSecretDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const { userData, loadingUserData } = useUserData();
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    if (userData?.webhookURL !== undefined) setWebhookUrl(userData.webhookURL ?? '');
  }, [userData]);

  const hasBusinessPlan = userData?.plan?.toLowerCase() === 'business';

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Պատճենվեց' });
  };

  /* ---------- API key ---------- */
  const handleCreateApiKey = async () => {
    setIsGeneratingKey(true);
    const apiKey = `xosum_${window.crypto.randomUUID().replace(/-/g, '')}`;

    try {
      await Promise.all([
        updateDoc(doc(db, 'users', auth.currentUser.uid), { apiKey }),
        new Promise((res) => setTimeout(res, 1000))
      ]);
      amplitude.track('API Key Created', { userId: auth.currentUser.uid });
      setSnackbar({ open: true, message: 'API բանալին ստեղծվեց' });
    } catch {
      setSnackbar({ open: true, message: 'Սխալ՝ ստեղծելիս' });
    }
    setIsGeneratingKey(false);
  };

  const handleDeleteApiKey = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { apiKey: null });
      amplitude.track('API Key Deleted', { userId: auth.currentUser.uid });
      setShowKey(false);
      setSnackbar({ open: true, message: 'Բանալին հեռացվեց' });
    } catch {
      setSnackbar({ open: true, message: 'Սխալ՝ հեռացնելիս' });
    }
    setShowDeleteKeyDialog(false);
    setLoading(false);
  };

  /* ---------- Webhook URL + secret ---------- */
  const handleSaveWebhook = async () => {
    setLoading(true);
    try {
      const updates = { webhookURL: webhookUrl };
      if (!userData?.webhookSecret) {
        updates.webhookSecret = `xosum_whsec_${window.crypto.randomUUID().replace(/-/g, '')}`;
      }
      await updateDoc(doc(db, 'users', auth.currentUser.uid), updates);
      setWebhookUrlChanged(false);
      setSnackbar({ open: true, message: 'Webhook URL-ը պահպանվեց' });
      if (updates.webhookSecret) {
        setSnackbar({ open: true, message: 'Webhook URL-ը և գաղտնիքը պահպանվեցին' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Սխալ՝ պահպանելիս' });
    }
    setLoading(false);
  };

  const handleWebhookKeyPress = (e) => {
    if (e.key === 'Enter' && webhookUrlChanged) {
      handleSaveWebhook();
    }
  };

  /* ---------- Webhook secret ---------- */
  const handleRegenerateSecret = async () => {
    setLoading(true);
    try {
      const newSecret = `xosum_whsec_${window.crypto.randomUUID().replace(/-/g, '')}`;
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { webhookSecret: newSecret });
      amplitude.track('Webhook Secret Regenerated', { userId: auth.currentUser.uid });
      setShowSecret(false);
      setSnackbar({ open: true, message: 'Գաղտնիքը թարմացվեց' });
    } catch {
      setSnackbar({ open: true, message: 'Սխալ՝ թարմացնելիս' });
    }
    setLoading(false);
  };

  const handleDeleteSecret = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { webhookSecret: null });
      setShowSecret(false);
      setSnackbar({ open: true, message: 'Գաղտնիքը հեռացվեց' });
    } catch {
      setSnackbar({ open: true, message: 'Սխալ՝ հեռացնելիս' });
    }
    setShowDeleteSecretDialog(false);
    setLoading(false);
  };

  /* ---------- UI ---------- */
  if (loadingUserData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="h6" color="text.secondary">Բեռնվում է…</Typography>
          </Stack>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Compact Header */}
      <Fade in timeout={600}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ApiIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              API և ինտեգրացիա
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Ավտոմատացրեք ձեր բիզնես պրոցեսները և ստացեք խոսակցությունների վերծանումները
          </Typography>
          {hasBusinessPlan && (
            <Box sx={{ mt: 2 }}>
              <Link 
                href="https://docs.xosum.am" 
                target="_blank" 
                rel="noopener" 
                sx={{ 
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: '0.9rem'
                }}
              >
                <DocsIcon sx={{ fontSize: 18 }} />
                Ինչպես օգտագործել
              </Link>
            </Box>
          )}
        </Box>
      </Fade>

      {/* Plan Gate */}
      {!hasBusinessPlan ? (
        <Fade in timeout={600}>
          <Paper 
            sx={{ 
              p: 4, 
              textAlign: 'center', 
              mb: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Background decoration */}
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)',
                opacity: 0.5
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.05)',
                opacity: 0.7
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ mb: 3 }}>
                <RocketIcon sx={{ fontSize: 56, mb: 2, opacity: 0.9 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
                  API ինտեգրացիան հասանելի է Business պլանում
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, maxWidth: 500, mx: 'auto' }}>
                Լավագույն լուծումը մեծ վերծանման ծավալ ունեցող բիզնեսների համար
                </Typography>
              </Box>

              {/* Feature highlights */}
              <Grid container spacing={2} sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <SpeedIcon sx={{ fontSize: 24, mb: 1, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                      Արագ ինտեգրացիա
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <AutoIcon sx={{ fontSize: 24, mb: 1, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                      Ավտոմատ ծանուցումներ
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <SecurityIcon sx={{ fontSize: 24, mb: 1, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    Անվտանգ և գաղտնի 
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                size="large"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.95)', 
                  color: '#667eea',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: 'white',
                    boxShadow: '0 6px 25px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  const email = auth.currentUser?.email ?? '';
                  window.open(
                    `https://tally.so/r/3Evk5L?prefilled_email=${encodeURIComponent(email)}&source=api_request`,
                    '_blank'
                  );
                }}
                startIcon={<SupportIcon />}
              >
                Կապ մեզ հետ
              </Button>
            </Box>
          </Paper>
        </Fade>
      ) : (
        <Fade in timeout={800}>
          <Box>
            <Grid container spacing={3}>
              {/* API Key Card */}
              <Grid item xs={12} lg={6}>
                <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'primary.main',
                            color: 'white'
                          }}
                        >
                          <KeyIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            API բանալի
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Բանալին ապահովում է ինտեգրացիայի անվտանգությունը, այն պահեք ապահով
                          </Typography>
                        </Box>
                      </Box>

                      <Divider />

                      {userData?.apiKey ? (
                        <Stack spacing={2}>
                          <Alert severity="success" icon={<CheckIcon />} sx={{ fontSize: '0.875rem' }}>
                            API բանալին ակտիվ է և պատրաստ օգտագործման
                          </Alert>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={showKey ? userData.apiKey : '••••••••••••••••••••'}
                              InputProps={{ 
                                readOnly: true,
                                sx: { 
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem',
                                  bgcolor: 'grey.50'
                                }
                              }}
                            />
                            <Tooltip title={showKey ? "Թաքցնել" : "Ցուցադրել"}>
                              <IconButton 
                                size="small" 
                                onClick={() => setShowKey((v) => !v)}
                              >
                                {showKey ? <EyeOffIcon /> : <EyeIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Պատճենել">
                              <IconButton 
                                size="small" 
                                onClick={() => copyText(userData.apiKey)}
                              >
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Հեռացնել">
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={() => setShowDeleteKeyDialog(true)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Stack>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <CodeIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            API բանալի չի ստեղծվել
                          </Typography>
                          <Button
                            variant="contained"
                            disabled={isGeneratingKey}
                            onClick={handleCreateApiKey}
                            startIcon={isGeneratingKey ? <CircularProgress size={16} /> : <SparkleIcon />}
                          >
                            {isGeneratingKey ? 'Ստեղծվում է...' : 'Ստեղծել բանալի'}
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Webhook URL Card */}
              <Grid item xs={12} lg={6}>
                <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'success.main',
                            color: 'white'
                          }}
                        >
                          <WebhookIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Webhook URL
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ստացեք ծանուցունմեր, երբ վերծանումը պատրաստ է
                          </Typography>
                        </Box>
                      </Box>

                      <Divider />

                      <Stack spacing={2}>
                        {webhookUrl && (
                          <Alert severity="info" icon={<LinkIcon />} sx={{ fontSize: '0.875rem' }}>
                            Webhook կարգավորված է և պատրաստ ծանուցումների
                          </Alert>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Webhook URL"
                            placeholder="https://your-domain.com/webhook"
                            value={webhookUrl}
                            onChange={(e) => {
                              setWebhookUrl(e.target.value);
                              setWebhookUrlChanged(true);
                            }}
                            onKeyPress={handleWebhookKeyPress}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'grey.50'
                              }
                            }}
                          />
                          {webhookUrlChanged && (
                            <Button 
                              variant="contained" 
                              size="small" 
                              disabled={loading} 
                              onClick={handleSaveWebhook}
                            >
                              {loading ? <CircularProgress size={16} /> : 'Պահպանել'}
                            </Button>
                          )}
                        </Box>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Webhook Secret Card */}
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'warning.main',
                            color: 'white'
                          }}
                        >
                          <SecurityIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Webhook գաղտնիք
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ապահովեք ձեր webhook-ի անվտանգությունը
                          </Typography>
                        </Box>
                        {userData?.webhookSecret && (
                          <Chip 
                            label="Ակտիվ" 
                            color="success" 
                            size="small" 
                            icon={<CheckIcon />}
                          />
                        )}
                      </Box>

                      <Divider />

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={
                            userData?.webhookSecret
                              ? showSecret
                                ? userData.webhookSecret
                                : '••••••••••••••••••••••••••••'
                              : 'Գաղտնիք չի ստեղծվել'
                          }
                          InputProps={{ 
                            readOnly: true,
                            sx: { 
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                              bgcolor: userData?.webhookSecret ? 'grey.50' : 'grey.100'
                            }
                          }}
                        />
                        {userData?.webhookSecret && (
                          <>
                            <Tooltip title={showSecret ? "Թաքցնել" : "Ցուցադրել"}>
                              <IconButton 
                                size="small" 
                                onClick={() => setShowSecret((v) => !v)}
                              >
                                {showSecret ? <EyeOffIcon /> : <EyeIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Պատճենել">
                              <IconButton 
                                size="small" 
                                onClick={() => copyText(userData.webhookSecret)}
                              >
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Հեռացնել">
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={() => setShowDeleteSecretDialog(true)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Թարմացնել/Ստեղծել">
                          <IconButton 
                            size="small" 
                            onClick={handleRegenerateSecret}
                            disabled={loading}
                            color="primary"
                          >
                            {loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}

      {/* Delete Key Dialog */}
      <Dialog 
        open={showDeleteKeyDialog} 
        onClose={() => setShowDeleteKeyDialog(false)}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <DeleteIcon color="error" />
            <Typography variant="h6">Հեռացնել API բանալին</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Դուք վստահ եք, որ ցանկանում եք հեռացնել API բանալին։ Այս գործողությունը կընդհատի 
            բոլոր ակտիվ ինտեգրացիաները։
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setShowDeleteKeyDialog(false)}
          >
            Չեղարկել
          </Button>
          <Button 
            disabled={loading} 
            color="error" 
            variant="contained"
            onClick={handleDeleteApiKey}
            startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {loading ? 'Հեռացվում է...' : 'Հեռացնել'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Secret Dialog */}
      <Dialog 
        open={showDeleteSecretDialog} 
        onClose={() => setShowDeleteSecretDialog(false)}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <DeleteIcon color="error" />
            <Typography variant="h6">Հեռացնել webhook գաղտնիքը</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Դուք վստահ եք, որ ցանկանում եք հեռացնել գաղտնիքը։ Սա կանվտանգ դարձնի 
            ձեր webhook-ները։
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setShowDeleteSecretDialog(false)}
          >
            Չեղարկել
          </Button>
          <Button 
            disabled={loading} 
            color="error" 
            variant="contained"
            onClick={handleDeleteSecret}
            startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {loading ? 'Հեռացվում է...' : 'Հեռացնել'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default API;